import classNames from 'classnames'
import { useCallback } from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout'

import type { ReactElement } from 'react'
import type { IAnalyticsApiFn } from '~/types/analytic'
import type { IFeature } from '~/types/feature'

import ContentLayout from '~/components/ContentLayout'
import GraphBlock from '~/components/GraphBlock'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import { COMMON_GRID_PROPS } from '~/const'
import { GRAPH_KEY_MAP } from '~/const/graph-keys/graph-key-map'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useHighchartsRef } from '~/hooks/useHighchartsRef'
import { useAnalyticsContext } from '~/providers/Analytics'

import './styles.scss'

const ResponsiveGridLayout = WidthProvider(Responsive)

/*********************** LAYOUTS **************************/
//  Downlowd
const layoutsDownload = {
  lg: [
    { h: 4, i: '0', minH: 4, minW: 4, w: 6, x: 0, y: 0 },
    { h: 4, i: '1', minH: 4, minW: 4, w: 6, x: 7, y: 0 },
  ],
}

//  Spend & Revenue
const layoutSpendAndRevenue = {
  lg: [
    { h: 4, i: '0', minH: 3, minW: 4, w: 6, x: 0, y: 0 },
    { h: 4, i: '1', minH: 3, minW: 4, w: 6, x: 6, y: 0 },
    { h: 4, i: '2', minH: 3, minW: 5, w: 6, x: 0, y: 4 },
    { h: 4, i: '3', minH: 3, minW: 5, w: 6, x: 6, y: 4 },
  ],
}

//  Revenue Distribution
const layoutRevenueDistribution = {
  lg: [
    { h: 4, i: '0', minH: 3, minW: 4, w: 6, x: 0, y: 0 },
    { h: 4, i: '1', minH: 3, minW: 4, w: 6, x: 6, y: 0 },
    { h: 4, i: '2', minH: 3, minW: 5, w: 6, x: 0, y: 4 },
    { h: 4, i: '3', minH: 3, minW: 5, w: 6, x: 6, y: 4 },
  ],
}

//  Subscriptions
const layoutSubscriptions = {
  lg: [
    { h: 4, i: '0', minH: 3, minW: 4, w: 6, x: 0, y: 0 },
    { h: 4, i: '1', minH: 3, minW: 4, w: 6, x: 6, y: 0 },
    { h: 4, i: '2', minH: 3, minW: 5, w: 6, x: 0, y: 4 },
    { h: 4, i: '3', minH: 3, minW: 5, w: 6, x: 6, y: 4 },
    { h: 4, i: '4', minH: 3, minW: 5, w: 6, x: 0, y: 8 },
    { h: 4, i: '5', minH: 3, minW: 5, w: 6, x: 6, y: 8 },
  ],
}

// //  Refunds
const layoutRefunds = {
  lg: [
    { h: 4, i: '0', minH: 3, minW: 4, w: 6, x: 0, y: 0 },
    { h: 4, i: '1', minH: 3, minW: 4, w: 6, x: 6, y: 0 },
    { h: 4, i: '2', minH: 3, minW: 5, w: 6, x: 0, y: 4 },
    { h: 4, i: '3', minH: 3, minW: 5, w: 6, x: 6, y: 4 },
    { h: 4, i: '4', minH: 3, minW: 5, w: 6, x: 0, y: 8 },
    { h: 4, i: '5', minH: 3, minW: 5, w: 6, x: 6, y: 8 },
  ],
}

export interface BusinessReportProps {
  analyticsApiFn?: IAnalyticsApiFn
  appId: string
  isAppLoading: boolean
}

const BusinessReport = ({ analyticsApiFn, appId, isAppLoading }: BusinessReportProps): ReactElement => {
  /** Local state */

  const { state } = useAnalyticsContext()

  const { handleSetHighchartsRef, highcharts } = useHighchartsRef()

  const layoutClassNames = classNames('analytics-business-report-view', {
    'filters-collapsed': state.isFiltersCollapsed,
  })

  /** Effects */

  useDocumentTitle('Business report')

  /** Handlers */

  const onResizeStop = () => highcharts?.charts.forEach((chart) => chart?.reflow())

  const getAnalyticsParams = useCallback(
    (feature: IFeature) => ({
      dateRange: state.computedFiltersDate.range,
      feature,
      filters: [{ key: 'installation_country', value: state.filters.country }],
    }),
    [state.computedFiltersDate, state.filters.country]
  )

  const getGraphOptions = useCallback(
    (feature: IFeature) => ({
      analyticsApiFn,
      analyticsParams: getAnalyticsParams(feature),
      appIds: [appId],
      defaultRange: 90,
      onGetHighchartsRef: handleSetHighchartsRef,
    }),
    [analyticsApiFn, appId, getAnalyticsParams, handleSetHighchartsRef]
  )

  const generateChartChildren = useCallback(
    (features: string[]) =>
      features.map((feature, idx) => (
        <div key={idx}>
          <GraphBlock
            frontendDataKey={feature}
            {...getGraphOptions(GRAPH_KEY_MAP[feature].backendDataKey)}
            {...GRAPH_KEY_MAP[feature]}
          />
        </div>
      )),
    [getGraphOptions]
  )

  return (
    <Layout className={layoutClassNames} withMargin>
      <ContentLayout loading={isAppLoading}>
        <Section title="Download">
          <ResponsiveGridLayout
            {...COMMON_GRID_PROPS}
            isResizable={false}
            layouts={layoutsDownload}
            onResizeStop={onResizeStop}
          >
            {generateChartChildren(['ui_br_downloads_daily', 'ui_br_downloads_monthly'])}
          </ResponsiveGridLayout>
        </Section>
        <Section title="Spend & Revenue">
          <ResponsiveGridLayout
            {...COMMON_GRID_PROPS}
            isResizable={false}
            layouts={layoutSpendAndRevenue}
            onResizeStop={onResizeStop}
          >
            {generateChartChildren([
              'ui_br_spend_revenue_daily',
              'ui_br_spend_revenue_weekly',
              'ui_br_spend_revenue_monthly',
              'ui_br_spend_revenue_margin',
            ])}
          </ResponsiveGridLayout>
        </Section>
        <Section title="Revenue Distribution">
          <ResponsiveGridLayout
            {...COMMON_GRID_PROPS}
            isResizable={false}
            layouts={layoutRevenueDistribution}
            onResizeStop={onResizeStop}
          >
            {generateChartChildren([
              'ui_br_revenue_distribution_summary_daily',
              'ui_br_revenue_distribution_summary_monthly',
              'ui_br_revenue_distribution_details_daily',
              'ui_br_revenue_distribution_details_monthly',
            ])}
          </ResponsiveGridLayout>
        </Section>
        <Section title="Subscriptions">
          <ResponsiveGridLayout
            {...COMMON_GRID_PROPS}
            compactType={null}
            layouts={layoutSubscriptions}
            onResizeStop={onResizeStop}
          >
            {generateChartChildren([
              'ui_br_subscriptions_freetrial_summary_daily',
              'ui_br_subscriptions_freetrial_summary_monthly',
              'ui_br_subscriptions_freetrial_details_daily',
              'ui_br_subscriptions_freetrial_details_monthly',
              'ui_br_subscriptions_active_summary_daily',
              'ui_br_subscriptions_active_details_daily',
            ])}
          </ResponsiveGridLayout>
        </Section>
        <Section title="Refunds">
          <ResponsiveGridLayout
            {...COMMON_GRID_PROPS}
            compactType={null}
            layouts={layoutRefunds}
            onResizeStop={onResizeStop}
          >
            {generateChartChildren([
              'ui_br_refunds_units_revenue_daily',
              'ui_br_refunds_units_revenue_monthly',
              'ui_br_refunds_units_by_type_daily',
              'ui_br_refunds_revenue_by_type_daily',
              'ui_br_refunds_units_by_subtype_daily',
              'ui_br_refunds_revenue_by_subtype_daily',
            ])}
          </ResponsiveGridLayout>
        </Section>
      </ContentLayout>
    </Layout>
  )
}

export default BusinessReport
